import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RateProvider } from "./outbound-labels";
import { RateServiceCode } from "./rate-service-snapshot";

export enum ShoppedServiceAccountKind {
  Redo = "redo_account",
  Merchant = "merchant_account",
}

export enum ShoppedServiceAvailability {
  Rated = "rated",
  Unavailable = "unavailable",
}

export enum ShoppedServicesNoticeKind {
  None = "none",
  NoSnapshot = "no_snapshot",
  Precheck = "precheck",
  RatesPending = "rates_pending",
}

export enum ShoppedServiceChannel {
  TikTok = "tiktok",
  AmazonBuyShipping = "amazon_buy_shipping",
}

/** The marketplace a rate was bought through, if any. */
export function rateChannel(
  provider: RateProvider | undefined,
): ShoppedServiceChannel | null {
  if (provider === RateProvider.TIKTOK) {
    return ShoppedServiceChannel.TikTok;
  }
  if (provider === RateProvider.AMAZON_BUY_SHIPPING) {
    return ShoppedServiceChannel.AmazonBuyShipping;
  }
  return null;
}

const RateServiceCodeSchema = createEnum(RateServiceCode, {
  enumName: "RateServiceCode",
});

/** Codes, not text, so the client can translate; the carrier's own message wins when there is one. */
const ShoppedServiceReasonSchema = z.object({
  // An open tab built before a code was added shows the generic reason instead of failing.
  code: RateServiceCodeSchema.nullable().catch(null),
  message: z.string().nullable(),
});
export type ShoppedServiceReason = z.infer<typeof ShoppedServiceReasonSchema>;

const RatedServiceSchema = z.object({
  availability: z.literal(ShoppedServiceAvailability.Rated),
  carrier: CarrierSchema,
  service: ServiceSchema,
  /** Null when the rate bills an external account, as the Service dropdown hides it. */
  price: z.object({ amount: z.string(), currency: z.string() }).nullable(),
  /** UPS Simple Rate pricing. */
  simpleRate: z.boolean(),
  channel: createEnum(ShoppedServiceChannel, {
    enumName: "ShoppedServiceChannel",
  }).nullable(),
  /** The billing account the carrier bills instead of the shipper; the client names it. */
  billingAccountId: z.string().nullable(),
});

const UnavailableServiceSchema = z.object({
  availability: z.literal(ShoppedServiceAvailability.Unavailable),
  carrier: CarrierSchema,
  service: ServiceSchema,
  reason: ShoppedServiceReasonSchema,
});

export const ShoppedServiceSchema = z.discriminatedUnion("availability", [
  RatedServiceSchema,
  UnavailableServiceSchema,
]);
export type ShoppedService = z.infer<typeof ShoppedServiceSchema>;

export enum ShoppedServiceAccountDisambiguatorKind {
  Last4 = "last4",
  Ordinal = "ordinal",
}

const ShoppedServiceAccountDisambiguatorSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal(ShoppedServiceAccountDisambiguatorKind.Last4),
    last4: z.string(),
  }),
  z.object({
    kind: z.literal(ShoppedServiceAccountDisambiguatorKind.Ordinal),
    ordinal: z.number().int().positive(),
  }),
]);
export type ShoppedServiceAccountDisambiguator = z.infer<
  typeof ShoppedServiceAccountDisambiguatorSchema
>;

export const ShoppedServiceGroupSchema = z.object({
  key: z.string(),
  carrier: CarrierSchema,
  accountKind: createEnum(ShoppedServiceAccountKind, {
    enumName: "ShoppedServiceAccountKind",
  }),
  accountName: z.string().nullable(),
  /** Set only when another group would read the same. */
  accountDisambiguator: ShoppedServiceAccountDisambiguatorSchema.nullable(),
  services: z.array(ShoppedServiceSchema),
});
export type ShoppedServiceGroup = z.infer<typeof ShoppedServiceGroupSchema>;

const ShoppedServicesNoticeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal(ShoppedServicesNoticeKind.None) }),
  z.object({ kind: z.literal(ShoppedServicesNoticeKind.NoSnapshot) }),
  z.object({
    kind: z.literal(ShoppedServicesNoticeKind.Precheck),
    reason: z.object({
      code: RateServiceCodeSchema.catch(RateServiceCode.RatingFailed),
      message: z.string().nullable(),
    }),
  }),
  z.object({ kind: z.literal(ShoppedServicesNoticeKind.RatesPending) }),
]);
export type ShoppedServicesNotice = z.infer<typeof ShoppedServicesNoticeSchema>;

export const ShoppedServicesSchema = z.object({
  notice: ShoppedServicesNoticeSchema,
  groups: z.array(ShoppedServiceGroupSchema),
});
export type ShoppedServices = z.infer<typeof ShoppedServicesSchema>;
